import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppNotificationToast,
  useAppNotificationToast,
} from './AppNotificationToast';

const MODULE_CHANGE_NOTICE_KEY = 'qawaem_module_change_notice';

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatModuleLabelForDisplay(label) {
  const s = String(label || '').trim();
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function formatModuleBulletList(modules) {
  if (!modules?.length) {
    return '<ul class="mt-1.5 list-disc pl-5 space-y-1 text-sm font-normal text-white/80"><li class="text-white/50">—</li></ul>';
  }
  const items = modules
    .map((label) => `<li>${escapeHtml(formatModuleLabelForDisplay(label))}</li>`)
    .join('');
  return `<ul class="mt-1.5 list-disc pl-5 space-y-1 text-sm font-normal text-white/85">${items}</ul>`;
}

function buildModuleChangeMessageHtml(previousModules, newModules, t) {
  const p = (key) => t(`modules.shared.licenseModulesChangeNotifier.${key}`);
  return [
    `<span class="block text-[15px] font-semibold leading-snug text-white">${escapeHtml(p('title'))}</span>`,
    `<span class="block mt-1 text-sm font-normal text-white/70">${escapeHtml(p('detail'))}</span>`,
    '<div class="mt-3 grid gap-4 sm:grid-cols-2">',
    `<div class="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5"><p class="text-[11px] font-semibold uppercase tracking-wider text-blue-200/90">${escapeHtml(p('before'))}</p>${formatModuleBulletList(previousModules)}</div>`,
    `<div class="rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-3 py-2.5"><p class="text-[11px] font-semibold uppercase tracking-wider text-emerald-200/90">${escapeHtml(p('after'))}</p>${formatModuleBulletList(newModules)}</div>`,
    '</div>',
    `<span class="block mt-3 text-xs font-normal text-white/55">${escapeHtml(p('footer'))}</span>`,
  ].join('');
}

/**
 * Toast Facturation-style when the license module pack changes (startup sync or live).
 */
export default function LicenseModulesChangeNotifier() {
  const { t } = useTranslation();
  const { toast, show, dismiss } = useAppNotificationToast(14000);

  const presentNotice = useCallback(
    (detail) => {
      if (!detail?.previousModules && !detail?.newModules) return;
      const prev = detail.previousModules || [];
      const next = detail.newModules || [];
      if (prev.join('|') === next.join('|')) return;

      show('', {
        variant: 'success',
        messageHtml: buildModuleChangeMessageHtml(prev, next, t),
        ms: 14000,
      });
    },
    [show, t],
  );

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(MODULE_CHANGE_NOTICE_KEY);
      if (raw) {
        sessionStorage.removeItem(MODULE_CHANGE_NOTICE_KEY);
        presentNotice(JSON.parse(raw));
      }
    } catch (e) {
      console.warn('Module change notice read failed:', e);
    }

    const onChanged = (event) => {
      presentNotice(event.detail);
    };

    window.addEventListener('qawaem-license-modules-changed', onChanged);
    return () => window.removeEventListener('qawaem-license-modules-changed', onChanged);
  }, [presentNotice]);

  return (
    <AppNotificationToast
      visible={toast.visible}
      message={toast.message}
      messageHtml={toast.messageHtml}
      variant={toast.variant}
      status={toast.status}
      showActions={toast.showActions}
      onDismiss={dismiss}
    />
  );
}
